import { makeAutoObservable } from "mobx";
import { CardinalityConstraint, OrderLineReference, Price, PromotionSolver, PromotionSolverVariableAssignment, PromotionSolverVarialbes, } from "../..";
import { IdMap, RArray, RMutableMap } from "../../collections";
import { ProductPromotionInstance } from "../PromotionInstance/ProductPromotionInstance";
import { PromotionInstanceId } from "../PromotionInstance/PromotionInstanceId";
import { WholeOrderPromotionInstance } from "../PromotionInstance/WholeOrderPromotionInstance";
const SOLVER_EXPLORATION_LIMIT = 10000;
export class PromotionScheme {
    constructor(params) {
        this.promotionInstanceIdSequence = 0;
        this.coupons = RMutableMap.fromIterable(params.coupons);
        this.initialPromotionTypes = params.initialPromotionTypes;
        this.purchaseOrderPayload = params.purchaseOrderPayload;
        makeAutoObservable(this, {
            promotionInstanceIdSequence: false,
        });
    }
    static empty(params) {
        return new PromotionScheme({
            ...params,
            coupons: IdMap.empty(),
        });
    }
    get couponPromotions() {
        const couponPromotionsArray = this.coupons.values.map((coupon) => coupon.promotion);
        return IdMap.fromIterable(couponPromotionsArray, {
            ignoreDuplicates: true,
        });
    }
    get promotionTypes() {
        return this.initialPromotionTypes.union(this.couponPromotions);
    }
    get couponCodes() {
        return this.coupons.values.map((c) => c.code.value).raw;
    }
    get checkoutData() {
        return {
            promotionalItems: this.promotionInstances.map((promotionInstance) => promotionInstance.checkoutData).raw,
            couponCodes: this.couponCodes,
        };
    }
    addCoupon(coupon) {
        this.coupons.set(coupon.id, coupon);
    }
    removeCoupon(id) {
        this.coupons.delete(id);
    }
    get price() {
        return Price.total(this.promotionInstances.map((promotionInstance) => promotionInstance.value));
    }
    get promotionAssociations() {
        const { purchaseOrderPayload, promotionTypes } = this;
        return promotionTypes.objects.mapOptional((promotionType) => {
            const { scope: promotionScope } = promotionType;
            switch (promotionScope.kind) {
                case "Product": {
                    const matchingOrderLines = purchaseOrderPayload.orderLines.filtered(({ productInstance }) => promotionScope.selector.test(productInstance));
                    const matchingProducts = matchingOrderLines.flatMap((orderLine) => RArray.range(orderLine.quantity.value).map((productInstanceIndex) => new OrderLineReference({
                        productInstance: orderLine.productInstance,
                        orderLineIndex: this.purchaseOrderPayload.orderLineIndex(orderLine.id),
                        productInstanceIndex,
                    })));
                    if (matchingProducts.size >= promotionScope.requiredItems.value) {
                        return {
                            kind: "Product",
                            promotionTypeId: promotionType.id,
                            matchingProducts,
                        };
                    }
                    return null;
                }
                case "WholeOrder": {
                    if (promotionScope.selector.test(this.purchaseOrderPayload)) {
                        return { kind: "WholeOrder", promotionTypeId: promotionType.id };
                    }
                    return null;
                }
            }
        });
    }
    get variableAssignment() {
        return new PromotionSolverVariableAssignment({
            promotionAssociations: this.promotionAssociations,
        });
    }
    cheaperVariable(a, b) {
        const { byVariable } = this.variableAssignment;
        return byVariable.get(a)[1].price.compare(byVariable.get(b)[1].price);
    }
    get cardinalityConstraints() {
        return this.variableAssignment.byPromotion.mapOptional(([promotionTypeId, variables]) => {
            const { scope: promotionScope } = this.promotionTypes.get(promotionTypeId);
            switch (promotionScope.kind) {
                case "Product": {
                    const orderedVariables = variables.sorted((a, b) => this.cheaperVariable(a, b));
                    return CardinalityConstraint.empty(new Set(orderedVariables), promotionScope.requiredItems.value, promotionScope.repeatable);
                }
                case "WholeOrder":
                    return CardinalityConstraint.empty(new Set(variables), 1, false);
            }
        });
    }
    get exclusionConstraints() {
        const exclusions = new Array(this.variableAssignment.variableCount)
            .fill(undefined)
            .map(() => []);
        const assigment = this.variableAssignment.byVariable;
        assigment.forEach(([promotionTypeIdA, orderLineReferenceA], variableA) => {
            assigment.forEach(([promotionTypeIdB, orderLineReferenceB], variableB) => {
                // NOTICE variable canot exclude itself
                if (variableA === variableB)
                    return;
                // NOTICE variables belonging to the same promotion never exclude
                if (promotionTypeIdA.eq(promotionTypeIdB))
                    return;
                if (orderLineReferenceA && orderLineReferenceB) {
                    // NOTICE promotions only exclude on the same orderLineReferences
                    if (!orderLineReferenceA.eq(orderLineReferenceB))
                        return;
                }
                const promotionTypeA = this.promotionTypes.get(promotionTypeIdA);
                if (!promotionTypeA.combinesWith.includes(promotionTypeIdB)) {
                    exclusions[variableA].push(variableB);
                }
            });
        });
        return exclusions;
    }
    get equivalenceClasses() {
        let currentEquivalenceClass = 0;
        const equivalenceMapping = new Map();
        const res = this.variableAssignment.byVariable.raw.map(([promotionTypeId, orderLineReference]) => {
            var _a;
            const equivalenceKey = `${promotionTypeId},${(_a = orderLineReference === null || orderLineReference === void 0 ? void 0 : orderLineReference.orderLineIndex) !== null && _a !== void 0 ? _a : "order"}`;
            let equivalenceClass = equivalenceMapping.get(equivalenceKey);
            if (equivalenceClass === undefined) {
                equivalenceMapping.set(equivalenceKey, currentEquivalenceClass);
                equivalenceClass = currentEquivalenceClass;
                currentEquivalenceClass += 1;
            }
            return equivalenceClass;
        });
        return res;
    }
    get promotionAssignmentSolution() {
        const solver = new PromotionSolver({
            limit: SOLVER_EXPLORATION_LIMIT,
            variables: PromotionSolverVarialbes.empty({
                variableCount: this.variableAssignment.variableCount,
                cardinalityConstraints: this.cardinalityConstraints,
                exclusionConstraints: this.exclusionConstraints,
                equivalenceClasses: this.equivalenceClasses,
            }),
        });
        return solver.solve();
    }
    get promotionInstances() {
        const solution = this.promotionAssignmentSolution;
        if (!solution) {
            return RArray.empty();
        }
        const promotionInstances = this.variableAssignment.byPromotion.flatMap(([promotionTypeId, assigmentVariables]) => {
            const solutionAssignment = assigmentVariables.filtered((variable) => solution.variables.at(variable));
            if (solutionAssignment.isEmpty) {
                return RArray.empty();
            }
            const nextId = () => new PromotionInstanceId(this.promotionInstanceIdSequence++);
            const promotionType = this.promotionTypes.get(promotionTypeId);
            switch (promotionType.scope.kind) {
                case "Product":
                    return this.distribute(solutionAssignment, promotionType.scope.requiredItems.value).map(({ discountSource, discountTarget }) => new ProductPromotionInstance({
                        id: nextId(),
                        promotionType,
                        active: true,
                        discountSource,
                        discountTarget,
                    }));
                case "WholeOrder":
                    return RArray.singleton(new WholeOrderPromotionInstance({
                        id: nextId(),
                        promotionType,
                        purchaseOrderPayload: this.purchaseOrderPayload,
                        active: true,
                    }));
            }
        });
        return promotionInstances;
    }
    distribute(solutionAssignment, requiredItems) {
        const assignmentOrderLineReferences = solutionAssignment
            .map((variable) => this.variableAssignment.byVariable.get(variable)[1])
            .sorted((a, b) => a.price.compare(b.price));
        const [targets, allSources] = assignmentOrderLineReferences.sliceAt(assignmentOrderLineReferences.size / requiredItems);
        const sources = requiredItems > 1
            ? allSources.chunks(requiredItems - 1)
            : RArray.singleton(RArray.empty());
        return targets.map((discountTarget, index) => ({
            discountSource: sources.get(index),
            discountTarget,
        }));
    }
}
